import { splitGenerationDisclaimer } from '@util/fitment/generationNotice';
import InformationBadge from '@c/InformationBadge';
import ProductWarehouseSelector from '@c/ProductWarehouseSelector';
import {
  ArrowUpRightIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import type { BreadcrumbItem } from '@ui/Breadcrumb';
import { prefix } from '@util/imagePrefix';
import UniversalFitmentNotice from 'app/product/[slug]/components/UniversalFitmentNotice';
import VehicleSpecificPrompt from 'app/product/[slug]/components/VehicleSpecificPrompt';
import { useWarehouse } from 'app/product/[slug]/components/WarehouseProvider';
import { getBikeFitment } from 'app/product/utils/getBikeFitment';
import { useBikeFilter } from 'context/BikeFilterContext';
import { ProductDocument, ProductTemplate } from 'models/product';
import type { Make, RideType } from 'models/shared';
import { UserDocument } from 'models/user';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import EditYourGarage from './components/EditYourGarage';
import FitmentChanges from './components/FitmentChanges';
import FitmentSourceBanner from './components/FitmentSourceBanner';
import GarageBikeCard from './components/GarageBikeCard';

type ProductFitmentTabProps = {
  product: ProductTemplate | ProductDocument;
  breadcrumbs?: BreadcrumbItem[];
  userDoc?: UserDocument | null;
  onSelection?: (product: ProductDocument) => void;
  isWarehouse?: boolean;
  isPreview?: boolean;
};

export default function ProductFitmentTab({
  product,
  breadcrumbs,
  userDoc,
  onSelection,
  isWarehouse,
  isPreview,
}: ProductFitmentTabProps) {
  const { selectedBike, setSelectedBike } = useBikeFilter();
  const { isActive, mode, aggregatedFitment, isLoading } = useWarehouse();
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Always show group fitment in general mode (no listing pre-filter).
  const useAggregated = isActive && mode === 'general';
  const fitmentBikes = useMemo(
    () =>
      useAggregated
        ? aggregatedFitment.bikes
        : product?.fitting_bikes?.bikes ?? [],
    [useAggregated, aggregatedFitment, product]
  );
  const isUniversal =
    !!product?.fitting_bikes?.universal ||
    (useAggregated && aggregatedFitment.allUniversal);

  const rideType: RideType =
    ((product as ProductDocument).ride_types?.[0] as RideType | undefined) ??
    'Dirt Bikes';

  const selectVehicleYear = (make: string, model: string, year: number) => {
    setSelectedBike({
      type: rideType,
      make: make as Make,
      model,
      year,
      date_added: null,
      downloadUrl: '',
      imageUrl: '',
      in_feed: false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasGenerationDisclaimer = splitGenerationDisclaimer(
    'disclaimer' in product ? product.disclaimer : undefined
  ).hasGenerationDisclaimer;
  const fittingMakes = new Set(fitmentBikes.map((b) => b.make));
  // A universal part fits every vehicle, so the garage cards are hidden to keep
  // them from flagging a bike that is missing from the vehicle list as a non-fit.
  const showGarage = !!userDoc && !isPreview && !isUniversal && !useAggregated;

  const supportChanges = (
    <FitmentChanges
      productId={product.id ?? ''}
      bikes={fitmentBikes}
      enabled={!!userDoc?.roles?.support && !isPreview && !useAggregated}
    />
  );

  const sortedBikes = (() => {
    if (!userDoc?.bikes) return [];

    return [...userDoc.bikes].sort((a, b) => {
      const fitmentA = getBikeFitment(fitmentBikes, [a]);
      const fitmentB = getBikeFitment(fitmentBikes, [b]);

      // Priority: fits > may-fit > doesn't fit
      const priority = { fits: 0, 'may-fit': 1, 'does-not-fit': 2, null: 3 };
      const priorityA =
        priority[fitmentA?.status as keyof typeof priority] ?? 3;
      const priorityB =
        priority[fitmentB?.status as keyof typeof priority] ?? 3;

      return priorityA - priorityB;
    });
  })();

  if (
    !selectedBike.model &&
    isWarehouse &&
    onSelection &&
    'group_id' in product
  ) {
    return (
      <div className="w-full space-y-8 px-8 py-8">
        {hasGenerationDisclaimer && (
          <FitmentSourceBanner product={product as ProductDocument} />
        )}
        <ProductWarehouseSelector
          key={product.id}
          product={product}
          onSelection={onSelection}
        />
      </div>
    );
  }
  if (useAggregated && isLoading && !fitmentBikes.length) {
    return (
      <div className="flex h-[10rem] w-full items-center justify-center px-4 py-16 sm:px-0">
        <span className="text-[1.6rem] font-medium text-zinc-500">
          Loading fitment…
        </span>
      </div>
    );
  }
  if (isUniversal && !fitmentBikes.length) {
    return (
      <div className="w-full space-y-8 p-8">
        {hasGenerationDisclaimer && (
          <FitmentSourceBanner product={product as ProductDocument} />
        )}
        <UniversalFitmentNotice product={product} />
        {supportChanges}
      </div>
    );
  }
  if (!fitmentBikes.length) {
    return (
      <div className="flex w-full flex-col gap-8 px-4 py-16 sm:px-0">
        <FitmentSourceBanner product={product as ProductDocument} />
        {supportChanges}
        <InformationBadge
          text="This product has no fitment information. Please verify compatibility with the seller."
          type="info"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-16 p-8">
      {(!useAggregated || hasGenerationDisclaimer) && (
        <FitmentSourceBanner product={product as ProductDocument} />
      )}
      {supportChanges}
      {isUniversal && <UniversalFitmentNotice product={product} />}

      {useAggregated && (
        <VehicleSpecificPrompt
          breadcrumbs={breadcrumbs}
          description="Select a vehicle to ensure fitment."
          showNoFit={false}
        />
      )}

      {showGarage && (
        <div className="flex flex-col gap-y-4">
          <EditYourGarage
            product={product as ProductDocument}
            userDoc={userDoc!}
          />

          <div className="flex w-full items-center gap-8 overflow-x-auto pb-4">
            {sortedBikes.map((bike) => (
              <div
                key={`bike-${bike.model}-${bike.make}-${bike.year}`}
                className="shrink-0"
              >
                <GarageBikeCard
                  bike={bike}
                  product={product as ProductDocument}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.from(fittingMakes).map((make) => {
        const parsedBrandName = make.split(' ').join('').toLowerCase();
        const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

        const compatibleVehicles = fitmentBikes
          .filter((b) => b.make === make)
          .filter((v) => v.make && v.year && v.model);

        const mergeVehicles = () => {
          const mergedVehicles: Array<{
            make: string;
            model: string;
            years: number[];
          }> = [];

          if (compatibleVehicles) {
            const processedVehicles = new Set<string>();

            for (const vehicle of compatibleVehicles) {
              const vehicleKey = `${vehicle.make}-${vehicle.model}`;

              if (processedVehicles.has(vehicleKey)) continue;
              processedVehicles.add(vehicleKey);

              const otherIdenticalVehicles = compatibleVehicles.filter(
                (v) => v.make === vehicle.make && v.model === vehicle.model
              );

              const years = otherIdenticalVehicles
                .map((v) => v.year)
                .filter((year): year is number => year !== null)
                .sort((a, b) => a - b);

              mergedVehicles.push({
                make: vehicle.make,
                model: vehicle.model ?? '',
                years,
              });
            }
          }

          return mergedVehicles.sort((a, b) => a.model.localeCompare(b.model));
        };

        const validVehicles = mergeVehicles();
        const hasOnlyInvalidVehicles = !validVehicles.length;
        const years = fitmentBikes
          .filter((b) => b.make === make && b.year)
          .map((b) => b.year)
          .sort((a, b) => a! - b!);
        const yearRange = years
          ? years.length > 1
            ? `${years[0]} - ${years[years.length - 1]}`
            : years[0]
          : null;

        return (
          <div key={make} className="flex flex-col gap-y-4">
            <div className="flex items-center gap-x-8 border-b border-zinc-200">
              {failedImages.has(make) ? (
                <div className="flex h-[7rem] w-[7rem] items-center justify-center rounded-[1rem] bg-gray-100 text-center">
                  <span className="text-[1.2rem] font-semibold text-gray-600">
                    {make}
                  </span>
                </div>
              ) : (
                <Image
                  src={brandImage}
                  alt={make}
                  height={300}
                  width={300}
                  className="h-[7rem] w-[7rem] object-contain"
                  onError={() => {
                    setFailedImages((prev) => new Set(prev).add(make));
                  }}
                />
              )}

              {hasOnlyInvalidVehicles ? (
                <div className="flex items-end gap-x-4">
                  <div className="hidden sm:flex">
                    <InformationCircleIcon
                      className="h-10 w-10 text-zinc-700 hover:cursor-help"
                      data-tooltip-id={`fitment-info-${make}`}
                      data-tooltip-content={`This product is compatible with ${make} vehicles, but we don't have detailed model and year information. Please contact the seller to verify exact fitment for your specific bike.`}
                    />
                    <Tooltip
                      id={`fitment-info-${make}`}
                      className="w-[10rem]"
                    />
                  </div>

                  <span
                    className={
                      'text-[1.6rem] font-semibold text-black sm:text-[1.8rem]'
                    }
                  >
                    Compatible with {yearRange ? yearRange : ''} {make} vehicles
                  </span>
                </div>
              ) : (
                <span
                  className={
                    'text-[1.6rem] font-semibold text-black sm:text-[1.8rem]'
                  }
                >
                  Compatible with {compatibleVehicles?.length} {make} vehicle(s)
                </span>
              )}
            </div>

            {hasOnlyInvalidVehicles ? (
              <div className="flex h-[12rem] items-center justify-center">
                <p className="text-[1.6rem] font-medium text-zinc-600 sm:text-[1.8rem]">
                  Contact the seller for exact {yearRange ? '' : 'year and'}{' '}
                  model fitment.
                </p>
              </div>
            ) : (
              <div className="mt-4 grid w-full grid-cols-1 items-center gap-y-4 sm:grid-cols-2">
                {validVehicles?.map((bike) => {
                  const key = `bike-${Math.min(...bike.years)}-${bike.model}-${
                    bike.make
                  }`;

                  if (!useAggregated) {
                    return (
                      <div className="flex items-center gap-x-4" key={key}>
                        <span className="text-[1.8rem] font-semibold text-brand-secondary">
                          {bike.model}
                        </span>

                        {bike.years.length > 1 ? (
                          <span className="text-[1.8rem] font-medium text-zinc-700">
                            {Math.min(...bike.years)} -{' '}
                            {Math.max(...bike.years)}
                          </span>
                        ) : (
                          <span className="text-[1.8rem] font-medium text-zinc-700">
                            {bike.years[0]}
                          </span>
                        )}
                      </div>
                    );
                  }

                  return (
                    <SelectableFitmentVehicle
                      key={key}
                      bike={bike}
                      onSelectYear={selectVehicleYear}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SelectableFitmentVehicle({
  bike,
  onSelectYear,
}: {
  bike: { make: string; model: string; years: number[] };
  onSelectYear: (make: string, model: string, year: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const hasMultipleYears = bike.years.length > 1;
  const sortedYears = [...bike.years].sort((a, b) => b - a);
  const yearLabel = hasMultipleYears
    ? `${Math.min(...bike.years)} - ${Math.max(...bike.years)}`
    : `${bike.years[0]}`;

  const handleClick = () => {
    if (hasMultipleYears) {
      setOpen((prev) => !prev);
    } else {
      onSelectYear(bike.make, bike.model, bike.years[0]);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        className="group flex w-fit items-center gap-x-4 text-left"
      >
        <span className="text-[1.8rem] font-semibold text-brand-secondary group-hover:underline">
          {bike.model}
        </span>
        <span className="text-[1.8rem] font-medium text-zinc-700">
          {yearLabel}
        </span>
        {hasMultipleYears ? (
          <ChevronDownIcon
            className={`h-5 w-5 shrink-0 text-zinc-400 transition-all group-hover:opacity-100 ${
              open ? 'rotate-180 opacity-100' : 'opacity-0'
            }`}
          />
        ) : (
          <ArrowUpRightIcon className="h-5 w-5 shrink-0 text-zinc-400 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </button>

      {open && hasMultipleYears && (
        <>
          <button
            type="button"
            aria-label="Close year selector"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-full z-20 mt-1 flex max-h-[24rem] w-[16rem] flex-col gap-y-1 overflow-y-auto rounded-[1rem] border border-zinc-200 bg-white p-2 shadow-lg">
            <span className="px-3 py-1 text-[1.3rem] font-semibold uppercase tracking-wide text-zinc-400">
              Select a year
            </span>
            {sortedYears.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelectYear(bike.make, bike.model, year);
                }}
                className="rounded-[0.6rem] px-3 py-3 text-left text-[1.6rem] font-medium text-zinc-800 transition-colors hover:bg-zinc-100"
              >
                {year}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
